import React from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Chip, ErrorNote, Loading, PageBody, PageHead, Tabs } from '../../components/aa';
import type { TabItem } from '../../components/aa';
import { useAsync } from '../aai/useAsync';
import { PeopleArea } from '../orbit-room/sessions/PeopleArea';
import { adoptionApi, isClientView } from './adoption';
import type { CompanyView, Viewer } from './adoption';
import { CompanyMark, OpportunityList, PeoplePanel, SessionList, Tiles, ToolsPanel, WorkPanel } from './parts';
import { GraphPanel } from './GraphPanel';

type Tab = 'opportunities' | 'sessions' | 'tools' | 'built' | 'people' | 'graph';

export const useRoot = (): string => {
  const location = useLocation();
  if (location.pathname.startsWith('/partner')) return '/partner/adoption';
  return '/adoption';
};

const subFor = (viewer: Viewer): string => {
  if (viewer.kind === 'champion') return 'What your company has been asked, what Orbit found, and what it turned into.';
  return 'What your company has been asked, what Orbit found, what it turned into, and the tools behind it.';
};

const tabsFor = (company: CompanyView) => {
  const tools = company.tools;
  const work = company.work;
  let toolsCount = 0;
  if (tools) toolsCount = tools.connected.length + tools.not_connected.length + tools.unreachable.length;
  let builtCount = 0;
  if (work) builtCount = work.projects.length + work.routines.length + work.workflows.length + work.skills.length;
  let sessionsLabel = 'Sessions';
  if (company.can_send) sessionsLabel = 'Sessions & people';
  const tabs: TabItem[] = [
    { id: 'opportunities', label: 'Opportunities', count: company.opportunities.length },
    { id: 'sessions', label: sessionsLabel, count: company.sessions.length },
    { id: 'tools', label: 'Tools & connectors', count: toolsCount },
    { id: 'built', label: 'What\'s built', count: builtCount },
  ];
  if (!company.can_send) tabs.push({ id: 'people', label: 'People', count: (company.people || []).length });
  tabs.push({ id: 'graph', label: 'Knowledge graph' });
  return tabs;
};

const Head: React.FC<{ company: CompanyView; root: string }> = ({ company, root }) => (
  <div className="mb-6">
    <Link to={`${root}?tab=clients`} className="mb-4 inline-flex min-h-[32px] items-center gap-1.5 text-[13px] font-semibold text-ink-muted hover:text-ink">
      <ArrowLeft size={15} aria-hidden />
      All companies
    </Link>
    <div className="flex min-w-0 items-center gap-4">
      <CompanyMark company={company} />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="m-0 text-[26px] font-bold leading-[33px] tracking-[-0.015em] text-ink">{company.name}</h1>
          {company.own && <Chip>Your company</Chip>}
        </div>
        <p className="m-0 mt-1 text-[13.5px] text-ink-muted">AI adoption: sessions, opportunities, tools and what was built.</p>
      </div>
    </div>
  </div>
);

export const CompanyAdoption: React.FC<{ companyId: string; root: string; embedded?: boolean }> = ({ companyId, root, embedded = false }) => {
  const [params, setParams] = useSearchParams();
  const loaded = useAsync(() => adoptionApi.company(companyId), 'Could not load this company.', [companyId]);

  const content = (() => {
    if (loaded.loading && !loaded.data) return <Loading label="Loading AI adoption" />;
    if (loaded.error || !loaded.data) return <ErrorNote message={loaded.error || 'Could not load this company.'} />;
    const { company, viewer } = loaded.data;
    const clientView = isClientView(viewer);
    const tabs = tabsFor(company);
    let tab = (params.get('tab') || 'opportunities') as Tab;
    if (!tabs.some((t) => t.id === tab)) tab = 'opportunities';
    const choose = (id: string) => {
      const next = new URLSearchParams(params);
      next.set('tab', id);
      next.delete('session');
      setParams(next, { replace: true });
      loaded.reload();
    };
    return (
      <>
        {embedded && <PageHead title="AI adoption" sub={subFor(viewer)} />}
        {!embedded && <Head company={company} root={root} />}
        <Tiles stats={company.stats} clientView={clientView} />
        <Tabs tabs={tabs} active={tab} onChange={choose} label="AI adoption" variant="pills" />
        {tab === 'opportunities' && <OpportunityList companies={[company]} clientView={clientView} showCompany={false} root={root} />}
        {tab === 'sessions' && company.can_send && (
          <PeopleArea clientId={company.id} initialTab="sessions" initialSession={params.get('session') || ''} />
        )}
        {tab === 'sessions' && !company.can_send && <SessionList companies={[company]} showCompany={false} />}
        {tab === 'tools' && company.tools && <ToolsPanel tools={company.tools} />}
        {tab === 'built' && company.work && <WorkPanel work={company.work} />}
        {tab === 'people' && <PeoplePanel people={company.people || []} />}
        {tab === 'graph' && <GraphPanel companyId={company.id} companyName={company.name} />}
      </>
    );
  })();

  if (embedded) return <>{content}</>;
  return <PageBody>{content}</PageBody>;
};

export const AdoptionCompanyPage: React.FC = () => {
  const root = useRoot();
  const { companyId = '' } = useParams<{ companyId: string }>();
  return <CompanyAdoption companyId={companyId} root={root} />;
};

export default AdoptionCompanyPage;
